/** API keys: every Claude connector and automation key - who it is for, what
 *  it may do, what it is used for - and making, rotating and revoking them.
 *  Only for the people in KEY_OWNERS (backend app/routes/keys.py). */
import { useCallback, useEffect, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { Icon } from "../components/icons";
import { api, type ApiKeyRow, type IssuedKey, type KeysList } from "../lib/api";

const ROLE_TONE: Record<string, string> = { manager: "wa", autopilot: "ac", analyst: "nu", specialist: "nu", admin: "ba" };
const ROLE_LABEL: Record<string, string> = {
  analyst: "Read-only",
  autopilot: "Autopilot",
  specialist: "Specialist",
  manager: "Manager - can send",
  admin: "Admin",
};
const ago = (iso: string | null) => {
  if (!iso) return "never";
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 60 / 24)} days ago`;
};

export function Keys() {
  const [list, setList] = useState<KeysList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [usageFor, setUsageFor] = useState<ApiKeyRow | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(() => {
    api.keys().then(setList).catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  if (!list)
    return (
      <>
        <ErrorBanner error={error} />
        {!error && <div className="empty">Reading the keys…</div>}
      </>
    );

  const active = list.keys.filter((k) => k.active);
  const revoked = list.keys.filter((k) => !k.active);
  const calls = active.reduce((a, k) => a + k.usage_30d.calls, 0);

  return (
    <>
      <ErrorBanner error={error} />
      <div className="dim" style={{ fontSize: 13.5, maxWidth: 860 }}>
        Every key that can reach the system: Claude connectors and automations. A key acts with its role's
        permissions, and every change it makes is in the Activity log under its name. Only you can see this page.
      </div>

      <div className="tiles">
        <div className="tile">
          <span className="lab">Active keys</span>
          <span className="v">{active.length}</span>
          <span className="d">{revoked.length} revoked</span>
        </div>
        <div className={`tile ${active.some((k) => k.role === "manager") ? "" : "ok"}`}>
          <span className="lab">Keys that can send</span>
          <span className="v">{active.filter((k) => k.role === "manager" || k.role === "admin").length}</span>
          <span className="d">manager keys - blasts, sending speed, kill switch</span>
        </div>
        <div className={`tile ${active.some((k) => k.stale) ? "ba" : "ok"}`}>
          <span className="lab">Unused {list.stale_days}+ days</span>
          <span className="v">{active.filter((k) => k.stale).length}</span>
          <span className="d">active but idle - worth revoking</span>
        </div>
        <div className="tile">
          <span className="lab">Claude tool calls, 30 days</span>
          <span className="v">{fmt.n(calls)}</span>
          <span className="d">counted per key from 22 Sep 2026</span>
        </div>
      </div>

      {issued && <IssuedCard k={issued} onDone={() => setIssued(null)} />}

      <div className="col" style={{ gap: 20 }}>
        <Card
          title={
            <div>
              <h3>Keys</h3>
              <div className="dim" style={{ fontSize: 12, marginTop: 1 }}>Secrets are never shown again after creation</div>
            </div>
          }
        >
          <KeyTable keys={active} onChanged={load} onError={setError} onIssued={setIssued} onUsage={setUsageFor} />
          {revoked.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button className="btn s" onClick={() => setShowRevoked(!showRevoked)}>
                {showRevoked ? "Hide" : "Show"} revoked ({revoked.length})
              </button>
              {showRevoked && (
                <div style={{ marginTop: 10 }}>
                  <KeyTable keys={revoked} onChanged={load} onError={setError} onIssued={setIssued} onUsage={setUsageFor} />
                </div>
              )}
            </div>
          )}
        </Card>
        <CreateCard roles={list.roles} onIssued={(k) => { setIssued(k); load(); }} onError={setError} />
      </div>

      {usageFor && <UsageCard k={usageFor} onClose={() => setUsageFor(null)} />}
    </>
  );
}

// ── the list ──────────────────────────────────────────────────
function KeyTable({
  keys,
  onChanged,
  onError,
  onIssued,
  onUsage,
}: {
  keys: ApiKeyRow[];
  onChanged: () => void;
  onError: (e: string) => void;
  onIssued: (k: IssuedKey) => void;
  onUsage: (k: ApiKeyRow) => void;
}) {
  const [confirming, setConfirming] = useState<{ id: number; what: "revoke" | "rotate" } | null>(null);
  const [busy, setBusy] = useState(false);
  const act = (k: ApiKeyRow, what: "revoke" | "rotate") => {
    setBusy(true);
    (what === "revoke" ? api.revokeKey(k.id).then(() => undefined) : api.rotateKey(k.id).then(onIssued))
      .then(() => {
        setConfirming(null);
        onChanged();
      })
      .catch((e) => onError(e.message))
      .finally(() => setBusy(false));
  };
  if (keys.length === 0) return <div className="empty">No keys.</div>;
  return (
    <div className="tw">
      <table>
        <thead>
          <tr>
            <th>Key</th>
            <th>Role</th>
            <th>Last used</th>
            <th style={{ textAlign: "right" }}>Requests</th>
            <th>Claude tools, 30 days</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} style={{ opacity: k.active ? 1 : 0.6 }}>
              <td style={{ whiteSpace: "normal", minWidth: 200 }}>
                <div style={{ fontWeight: 600 }}>{k.name}</div>
                <div className="dim" style={{ fontSize: 12 }}>
                  {k.person ? `for ${k.person} · ` : ""}
                  <span className="mono">{k.prefix}</span> · made {new Date(k.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {k.created_by ? ` by ${k.created_by.replace(/@.*/, "")}` : ""}
                </div>
              </td>
              <td><Pill tone={ROLE_TONE[k.role] ?? "nu"}>{ROLE_LABEL[k.role] ?? k.role}</Pill></td>
              <td>
                {!k.active ? (
                  <Pill tone="nu">revoked {ago(k.revoked_at)}</Pill>
                ) : k.stale ? (
                  <Pill tone="wa">{ago(k.last_used_at)}</Pill>
                ) : (
                  <span>{ago(k.last_used_at)}</span>
                )}
              </td>
              <td style={{ textAlign: "right" }} className="num">{fmt.n(k.use_count)}</td>
              <td style={{ whiteSpace: "normal", minWidth: 160 }}>
                {k.usage_30d.calls === 0 ? (
                  <span className="dim">none</span>
                ) : (
                  <button className="btn s" onClick={() => onUsage(k)} title="See by day and tool">
                    {fmt.n(k.usage_30d.calls)} calls
                    {k.usage_30d.errors ? ` · ${k.usage_30d.errors} errors` : ""}
                  </button>
                )}
                {k.usage_30d.tools.length > 0 && (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {k.usage_30d.tools.slice(0, 3).map((t) => t.tool.replace(/_/g, " ")).join(", ")}
                  </div>
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {k.active &&
                  (confirming?.id === k.id ? (
                    <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <span className="dim" style={{ fontSize: 12 }}>
                        {confirming.what === "revoke" ? "Stops working at once." : "New secret; the old one stops at once."}
                      </span>
                      <button className={`btn s ${confirming.what === "revoke" ? "d" : "p"}`} disabled={busy}
                              onClick={() => act(k, confirming.what)}>
                        {confirming.what === "revoke" ? "Revoke" : "Rotate"}
                      </button>
                      <button className="btn s" onClick={() => setConfirming(null)}>Cancel</button>
                    </span>
                  ) : (
                    <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      {k.role !== "admin" && (
                        <button className="btn s" onClick={() => setConfirming({ id: k.id, what: "rotate" })}>Rotate</button>
                      )}
                      <button className="btn s d" onClick={() => setConfirming({ id: k.id, what: "revoke" })}>Revoke</button>
                    </span>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── making one ────────────────────────────────────────────────
function CreateCard({
  roles,
  onIssued,
  onError,
}: {
  roles: Record<string, string>;
  onIssued: (k: IssuedKey) => void;
  onError: (e: string) => void;
}) {
  const [name, setName] = useState("");
  const [person, setPerson] = useState("");
  const [role, setRole] = useState("analyst");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const order = ["analyst", "autopilot", "specialist", "manager"].filter((r) => roles[r]);
  const needsConfirm = role === "manager";
  const ok = name.trim().length >= 3 && (!needsConfirm || confirm.trim() === name.trim());
  const create = () => {
    setBusy(true);
    api
      .createKey({ name: name.trim(), role, person: person.trim() || undefined, confirm: needsConfirm ? confirm.trim() : undefined })
      .then((k) => {
        onIssued(k);
        setName("");
        setPerson("");
        setConfirm("");
        setRole("analyst");
      })
      .catch((e) => onError(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Card title="New key" footer={<span className="dim">One key per person or tool, so a change in the log says who made it. There are no admin keys.</span>}>
      <div className="col" style={{ gap: 12 }}>
        <div className="g2">
          <label className="col" style={{ gap: 5 }}>
            <span className="lab">Name</span>
            <input className="f" placeholder="e.g. Ammar - Claude autopilot" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="col" style={{ gap: 5 }}>
            <span className="lab">For (optional)</span>
            <input className="f" placeholder="who or what will use it" value={person} onChange={(e) => setPerson(e.target.value)} />
          </label>
        </div>
        <div className="col" style={{ gap: 6 }}>
          <span className="lab">What it may do</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 8 }}>
          {order.map((r) => (
            <label
              key={r}
              className="crow"
              style={{
                cursor: "pointer",
                alignItems: "flex-start",
                borderColor: role === r ? (r === "manager" ? "var(--warn)" : "var(--acc)") : undefined,
                background: role === r ? (r === "manager" ? "var(--warn-soft)" : "var(--acc-soft)") : undefined,
              }}
            >
              <input type="radio" name="role" checked={role === r} onChange={() => setRole(r)} style={{ marginTop: 3 }} />
              <span style={{ flex: 1 }}>
                <b style={{ fontSize: 13 }}>{ROLE_LABEL[r]}</b>
                <span className="dim" style={{ display: "block", fontSize: 12, lineHeight: 1.45 }}>{roles[r]}</span>
              </span>
            </label>
          ))}
          </div>
        </div>
        {needsConfirm && (
          <div className="banner warn stack" style={{ gap: 6 }}>
            <b>This key can send texts.</b>
            <span style={{ fontSize: 12.5 }}>Type the key's name again to confirm.</span>
            <input className="f" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={name || "the name above"} />
          </div>
        )}
        <div className="row">
          <button className="btn p" disabled={!ok || busy} onClick={create}>
            <Icon name="key" size={15} /> {busy ? "Creating…" : "Create key"}
          </button>
        </div>
      </div>
    </Card>
  );
}

// ── the one time a secret is shown ────────────────────────────
function Copy({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="col" style={{ gap: 5 }}>
      <span className="lab">{label}</span>
      <div className="row" style={{ gap: 6 }}>
        <input className="f mono" readOnly value={value} style={{ flex: 1, fontSize: 12 }} onFocus={(e) => e.target.select()} />
        <button
          className="btn"
          onClick={() => {
            navigator.clipboard?.writeText(value).then(() => {
              setDone(true);
              setTimeout(() => setDone(false), 1500);
            });
          }}
        >
          <Icon name="copy" size={15} /> {done ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function IssuedCard({ k, onDone }: { k: IssuedKey; onDone: () => void }) {
  return (
    <div className="card" style={{ borderColor: "var(--ok)", boxShadow: "inset 3px 0 0 var(--ok), var(--sh)" }}>
      <div className="cb col" style={{ gap: 14 }}>
        <div className="row w" style={{ gap: 10 }}>
          <Pill tone="ok">Created</Pill>
          <b style={{ fontSize: 15 }}>{k.name}</b>
          <Pill tone={ROLE_TONE[k.role] ?? "nu"}>{ROLE_LABEL[k.role] ?? k.role}</Pill>
          <div className="sp" />
          <span style={{ color: "var(--bad)", fontWeight: 600, fontSize: 12.5 }}>
            Shown once - store it now. It cannot be shown again.
          </span>
        </div>
        <Copy label="Claude connector URL - paste this into Claude's connector settings" value={k.connector_url} />
        <Copy label="The key on its own - for an automation (Authorization: Bearer …)" value={k.key} />
        <div className="row">
          <div className="sp" />
          <button className="btn p" onClick={onDone}>I have saved it</button>
        </div>
      </div>
    </div>
  );
}

// ── what a key is used for ────────────────────────────────────
function UsageCard({ k, onClose }: { k: ApiKeyRow; onClose: () => void }) {
  const [rows, setRows] = useState<Array<{ day: string; tool: string; calls: number; errors: number }> | null>(null);
  useEffect(() => {
    api.keyUsage(k.id).then((r) => setRows(r.rows)).catch(() => setRows([]));
  }, [k.id]);
  return (
    <Card title={`What "${k.name}" has been used for - last 30 days`} actions={<button className="btn s" onClick={onClose}>Close</button>}>
      {!rows ? (
        <div className="empty">Reading…</div>
      ) : rows.length === 0 ? (
        <div className="empty">No Claude tool calls recorded.</div>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Tool</th>
                <th style={{ textAlign: "right" }}>Calls</th>
                <th style={{ textAlign: "right" }}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.day}-${r.tool}`}>
                  <td className="num">{r.day}</td>
                  <td>{r.tool.replace(/_/g, " ")}</td>
                  <td style={{ textAlign: "right" }} className="num">{r.calls}</td>
                  <td style={{ textAlign: "right" }} className="num">{r.errors || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
